"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  AuthCard,
  AuthField,
  AuthLink,
  AuthStatus,
  authFieldClassName,
  authPrimaryClass,
} from "@/components/auth/auth-card";
import { PasswordField } from "@/components/auth/password-field";
import { authClient } from "@/lib/auth-client";
import { ACCOUNT_BLOCKED_CODE, ACCOUNT_BLOCKED_MESSAGE } from "@/modules/auth/messages";
import { safeAuthNext } from "@/lib/redirect";
import { loginSchema } from "@/modules/account/schemas";

type LoginValues = {
  email: string;
  password: string;
};

export function LoginForm({ nextPath }: { nextPath?: string }) {
  const router = useRouter();
  const [formError, setFormError] = useState<string | null>(null);
  const form = useForm<LoginValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values: LoginValues) {
    setFormError(null);
    const result = await authClient.signIn.email({
      email: values.email.trim().toLowerCase(),
      password: values.password,
      callbackURL: safeAuthNext(nextPath),
    });

    if (result.error) {
      setFormError(
        result.error.code === ACCOUNT_BLOCKED_CODE ? ACCOUNT_BLOCKED_MESSAGE : "Не удалось войти. Проверьте email и пароль.",
      );
      return;
    }

    router.push(safeAuthNext(nextPath));
    router.refresh();
  }

  return (
    <AuthCard
      title="Вход"
      description="Войдите, чтобы продолжить работу с проектами и сметами."
      footer={
        <>
          Нет аккаунта? <AuthLink href="/register">Создать аккаунт</AuthLink>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
        <AuthField label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className={authFieldClassName}
            aria-invalid={Boolean(form.formState.errors.email) || undefined}
            aria-describedby={form.formState.errors.email ? "email-error" : undefined}
            {...form.register("email")}
          />
        </AuthField>
        <AuthField
          label="Пароль"
          htmlFor="password"
          error={form.formState.errors.password?.message}
          aside={<AuthLink href="/forgot-password">Забыли пароль?</AuthLink>}
        >
          <PasswordField
            id="password"
            autoComplete="current-password"
            invalid={Boolean(form.formState.errors.password)}
            registration={form.register("password")}
            variant="auth"
          />
        </AuthField>
        {formError ? <AuthStatus tone="error">{formError}</AuthStatus> : null}
        <button type="submit" className={authPrimaryClass} disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? "Входим…" : "Войти"}
        </button>
      </form>
    </AuthCard>
  );
}
